/*
 * The _butter-bar_ values and it's visibility is now driven by custom fields in the admin.
 * See screen-shot: https://github.com/PagerDuty/citizens-band/pull/703#issuecomment-435893405
 *
 * When the user clicks close their preference is saved in `Local Storage`.
 *
 * The next time the page loads User preferences are re-loaded from `Local Storage`.
 *
 * If there are more than two differences between `Local Storage` and the
 * _butter-bar_ the user preferences are reset.
 *
 * - The values compared are _Copy_, _CTA-text_ and _CTA-href_.
 *
 * - This allows for one correction (e.g.: typo) without disregarding the user
 *   preferences and to reset the visibility when the message changes.
 */

import { runPersonalizationRules } from "./personalization";
import getCondition from "./personalization/conditions";

/** @typedef {{copy: string, ctaText: string, ctaHref: string}} BbElement
/** @typedef {BbElement[]} BbId

/**
 * Gets the values that distinguish butter-bars from each other.
 * @return {BbId}
 */
function getButterBarId() {
  const bar = document.getElementById("butter-bar");
  const elements = bar?.querySelectorAll(".butter-bar-link");

  /** @type BbId */
  let id = [];
  if (!elements) return id;

  // Get all elements' content
  for (const element of elements) {
    if (!element) continue;

    const copy = element.querySelector(".js-copy");
    const cta = element.querySelector(".js-cta");

    id.push({
      copy: copy?.textContent?.trim().replace(/\s+/gi, "") || "",
      ctaText: cta?.textContent?.trim().replace(/\s+/gi, "") || "",
      ctaHref: element?.getAttribute("href")?.trim() || "",
    });
  }

  // Remove the last element, which is a duplicate of the first
  if (elements.length > 2) id.pop();

  return id;
}

/**
 * Persist the user's butter-bar visibility preference.
 * @arg {BbId} bbId
 * @return {boolean} true if the preference was saved.
 */
function saveViewButterBarUserPreference(bbId) {
  var ls = window.localStorage;
  if (ls) {
    ls["butterBarContent"] = JSON.stringify(bbId);
    ls["butterBarUserDisabled"] = true;
    return true;
  }
  return false;
}

/**
 * Has the user disabled the butter-bar previously?
 * Allows for one correction (e.g.: typo) without disregarding the user preferences.
 * @arg {BbId} bbId
 * @return {boolean} true if the user disabled the butter-bar.
 */
function hasUserDisabledButterBar(bbId) {
  const ls = window.localStorage;
  if (!ls) return false;

  const butterBarUserDisabled = ls["butterBarUserDisabled"];
  if (!butterBarUserDisabled) return false;

  const lsContent = JSON.parse(ls["butterBarContent"] ?? "[]");
  if (lsContent.length !== bbId.length) return false;

  let diffs = 0;
  for (let i = 0; i < lsContent.length; i++) {
    if (lsContent[i]?.copy !== bbId[i]?.copy) diffs += 1;
    if (lsContent[i]?.ctaText !== bbId[i]?.ctaText) diffs += 1;
    if (lsContent[i]?.ctaHref !== bbId[i]?.ctaHref) diffs += 1;
  }

  return diffs < 2;
}

/**
 * IIF to show/hide the butter-bar based on user's saved preference.
 * @param {(() => void) | undefined} [callback]
 */
export function viewButterBarBasedOnUserPreference(callback) {
  // Personalized Butter Bar content
  personalizeButterBar();

  // Butter bar is now hidden by default. If it is enabled and if the user hasn't dismissed it, then we will show it
  const isButterBarEnabled = document
    .getElementById("butter-bar")
    ?.getAttribute("data-butter-bar-is-visible");
  if (isButterBarEnabled === "true") {
    if (!hasUserDisabledButterBar(getButterBarId())) {
      showButterBar();
      if (callback) callback();
    }
  }
}

function showButterBar() {
  document.getElementById("butter-bar")?.classList.remove("butter-bar-hidden");
}

/**
 * @param {(() => void) | undefined} [callback]
 */
export function closeButterBar(callback) {
  document.getElementById("butter-bar")?.classList.add("butter-bar-hidden");
  saveViewButterBarUserPreference(getButterBarId());
  if (callback) callback();
}

/**
 * Updates the butter bar content based on GDPR flag
 *
 * @returns {void}
 */
function personalizeButterBar() {
  /** @type {import("./personalization/global-rules").PersonalizationRule[]} */
  const butterBarPersonalizationRules = [
    {
      id: "butter-bar-pd-connect-2025-emea",
      name: "Butter-bar PagerDuty Connect 2025 EMEA",
      conditions: [
        getCondition("inEurope"),
        () => {
          // Last event date is October 9, 2025
          const lastEventDate = new Date("2025-10-09T09:30:00+02:00");
          const now = new Date();
          return now < lastEventDate;
        },
      ],
      actions: [
        (store) => {
          const cfData = store?.get("cfData");
          const country = cfData?.country;

          const newContentIndex = 0;
          /** @type BbElement */
          let newContent = {
            copy: "PagerDuty Connect: Join us in Munich or Paris for AI & automation insights from customers & leaders.",
            ctaText: "Register for free",
            ctaHref:
              "/events/?type=pagerduty-events&region=europe#events-filters",
          };

          switch (country) {
            case "Germany":
              newContent = {
                copy: "PagerDuty Connect: Join us in Munich for AI & automation insights from customers & leaders.",
                ctaText: "Register for free",
                ctaHref: "https://info.pagerduty.com/connect-munich",
              };
              break;
            case "France":
              newContent = {
                copy: "PagerDuty Connect: Join us in Paris for AI & automation insights from customers & leaders.",
                ctaText: "Register for free",
                ctaHref: "https://info.pagerduty.com/connect-paris",
              };
              break;
          }

          updateButterBarContent(newContent, newContentIndex);
        },
      ],
    },
  ];

  runPersonalizationRules(butterBarPersonalizationRules);
}

/**
 * Updates the butter bar content
 * @param {BbElement} newContent
 * @param {Number} index
 */
function updateButterBarContent(newContent, index = 0) {
  const bar = document.getElementById("butter-bar");
  const elements = bar?.querySelectorAll(".butter-bar-link");

  if (!elements || elements.length == 0 || index >= elements.length) return;

  // Update element
  updateButterBarElement(elements[index], newContent);

  // If the first element is updated, update the last element as well
  if (index == 0 && elements.length > 1) {
    updateButterBarElement(elements[elements.length - 1], newContent);
  }
}

/**
 * Updates the butter bar element
 * @param {Element | undefined} element
 * @param {BbElement} newContent
 */
function updateButterBarElement(element, newContent) {
  if (!element) return;

  const copy = element.querySelector(".js-copy");
  const cta = element.querySelector(".js-cta");

  if (copy) copy.textContent = newContent.copy;
  if (cta) cta.textContent = newContent.ctaText;
  if (element) element.setAttribute("href", newContent.ctaHref);
}
